import { useEffect, useMemo, useState } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import ActivityIndicator from 'components/ActivityIndicatorComponent';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import {
  useGetMyViewDashboardList,
  useUpdateMyViewDashboardListOrdering,
} from 'services/v1/Tenant/ConfigurationService';
import {
  DataGridPro,
  GridColumns,
  GridRenderCellParams,
  GridRowModel,
  GridRowOrderChangeParams,
  GridValueFormatterParams,
} from '@mui/x-data-grid-pro';
import { Button, Chip } from '@mui/material';
import { MyViewDashboardDefinitionItem, RequestMyViewDashboardItemOrdering } from 'types/api/Tenant/ConfigurationTypes';
import { ButtonRecordEdit } from 'pages/Tenant/AROKMS/DataInput/components/ButtonRecordEdit';

interface PeopleConfigDetailComponentProps {
  onEdit: (data: MyViewDashboardDefinitionItem) => void;
  onAdd: () => void;
}

function updateRowPosition(
  oldIndex: number,
  newIndex: number,
  rows: Array<GridRowModel<MyViewDashboardDefinitionItem>>
) {
  const rowsClone = [...rows];
  const [row] = rowsClone.splice(oldIndex, 1);
  rowsClone.splice(newIndex, 0, row);
  for (let i = 0; i < rowsClone.length; i++) {
    rowsClone[i].colIndexOrder = i + 1;
  }
  return rowsClone;
}

export const PeopleConfigListComponent = (props: PeopleConfigDetailComponentProps) => {
  const { onEdit, onAdd } = props;

  const [rowData, setRowData] = useState<Array<MyViewDashboardDefinitionItem>>([]);
  const { data: myViewDashboard, isLoading } = useGetMyViewDashboardList();
  const { mutate: updateOrdering, isLoading: isUpdating } = useUpdateMyViewDashboardListOrdering();

  const handleRowOrderChange = async (params: GridRowOrderChangeParams) => {
    const newRows = updateRowPosition(params.oldIndex, params.targetIndex, rowData);
    setRowData(newRows);
    const payload: RequestMyViewDashboardItemOrdering[] = newRows
      .map((item) => {
        if (item.colIndexOrder == null) return undefined;
        return { colIndexOrder: item.colIndexOrder, id: item.id };
      })
      .filter((item): item is RequestMyViewDashboardItemOrdering => item !== undefined);
    updateOrdering(payload);
  };

  const columns: GridColumns = useMemo(
    () => [
      {
        field: 'action',
        headerName: '',
        editable: false,
        sortable: false,
        width: 40,
        renderCell: (params: GridRenderCellParams) => (
          <ButtonRecordEdit {...params} onClick={() => onEdit(params.row)} />
        ),
      },
      {
        field: 'dashboardName',
        headerName: 'Dashboard Name',
        flex: 1,
        minWidth: 160,
        valueFormatter: (params: GridValueFormatterParams) => params.value?.toLocaleString(),
      },
      {
        field: 'solutionDefinition',
        headerName: 'Component',
        flex: 1,
        minWidth: 140,
        valueFormatter: (params: GridValueFormatterParams) => params.value?.label?.toLocaleString(),
      },
      {
        field: 'users',
        headerName: 'Users',
        width: 90,
        renderCell: (params: GridRenderCellParams) => (
          <Chip size='small' label={params.value?.length ?? 0} color='primary' variant='outlined' />
        ),
      },
      {
        field: 'myViews',
        headerName: 'Pages',
        width: 90,
        renderCell: (params: GridRenderCellParams) => (
          <Chip size='small' label={params.value?.length ?? 0} color='secondary' variant='outlined' />
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    if (myViewDashboard?.data) {
      setRowData(myViewDashboard.data);
    }
  }, [myViewDashboard?.data]);

  return (
    <Box sx={{ bgcolor: '#fff', px: 2, py: 2, width: '100%' }}>
      {/* Toolbar */}
      <Stack direction='row' justifyContent='space-between' alignItems='flex-start' mb={1.5}>
        <Stack>
          <Typography variant='h6' color='text.primary' fontWeight='bold'>
            My View Dashboards
          </Typography>
          <Typography variant='body2' color='text.secondary' mt={0.5}>
            Set up home navigation for pages and dashboards to help users navigate more easily.
          </Typography>
        </Stack>
        <Button
          variant='secondary-table-panel'
          startIcon={<AddCircleOutlineOutlinedIcon />}
          onClick={onAdd}
          sx={{ flexShrink: 0, ml: 2 }}
        >
          Add Dashboard
        </Button>
      </Stack>
      <Divider sx={{ mb: 2 }} />

      {isLoading ? (
        <Stack justifyContent='center' height={200}>
          <ActivityIndicator />
        </Stack>
      ) : rowData.length === 0 ? (
        <Stack
          alignItems='center'
          justifyContent='center'
          height={220}
          border='1px dashed #D0D5DD'
          borderRadius={2}
          gap={1.5}
        >
          <DashboardOutlinedIcon sx={{ fontSize: 36, color: '#D0D5DD' }} />
          <Typography variant='body2' color='text.secondary' fontWeight={600}>
            No dashboards configured
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            Click <strong>Add Dashboard</strong> to create your first one.
          </Typography>
        </Stack>
      ) : (
        <>
          <Typography variant='caption' color='text.secondary' mb={1} display='block'>
            {rowData.length} dashboard{rowData.length !== 1 ? 's' : ''} &middot; Drag rows to reorder
          </Typography>
          <DataGridPro
            autoHeight
            loading={isLoading || isUpdating}
            disableMultipleColumnsFiltering
            density='compact'
            hideFooter
            rowCount={rowData.length}
            rows={rowData}
            columns={columns}
            filterMode='client'
            checkboxSelection={false}
            rowReordering
            onRowOrderChange={handleRowOrderChange}
            sx={{ border: '1px solid #EEF2F7', borderRadius: 1 }}
          />
        </>
      )}
    </Box>
  );
};
